import { HEAD_NECK_MODEL, LM, sideLandmarks, THRESHOLDS } from '@erectus/shared';
import type { FrameMetrics, Landmark, Side } from '@erectus/shared';
import {
  distance,
  RAD_TO_DEG,
  signedAngleFromHorizontal,
  signedAngleFromVertical,
  sub,
  threePointAngle,
  toPlane,
  vectorAngle,
  type Point,
} from './geometry.js';

export * from './geometry.js';
export * from './framing.js';

/**
 * Every metric in docs/ARCHITECTURE.md §1.4, as pure functions over one frame of
 * aspect-corrected landmarks. Only the camera-facing side is used; the quality
 * gate decides which side that is.
 */

export interface MetricContext {
  landmarks: readonly Landmark[];
  side: Side;
  /** videoWidth / videoHeight — required for aspect-corrected angles. */
  aspect: number;
  /** Visibility flags from the quality gate; disable metrics we cannot trust. */
  hipAvailable: boolean;
  kneeAvailable: boolean;
}

function p(ctx: MetricContext, index: number): Point | null {
  const lm = ctx.landmarks[index];
  if (!lm) return null;
  return toPlane(lm, ctx.aspect);
}

/**
 * Anterior (facing) direction as a sign on the x axis.
 * The nose is always anterior to the ear, which makes it a stable reference even
 * in deep forward-head posture — unlike ear-vs-shoulder, which is the very thing
 * we are measuring.
 */
export function anteriorSign(ctx: MetricContext): number {
  const ear = p(ctx, sideLandmarks(ctx.side).ear);
  const nose = p(ctx, LM.NOSE);
  if (ear && nose) {
    const dx = nose.x - ear.x;
    if (Math.abs(dx) > 1e-4) return Math.sign(dx);
  }
  // Fall back to the head-vs-hip offset, then to the camera side convention.
  const shoulder = p(ctx, sideLandmarks(ctx.side).shoulder);
  const hip = p(ctx, sideLandmarks(ctx.side).hip);
  if (ear && hip) {
    const dx = ear.x - hip.x;
    if (Math.abs(dx) > 1e-4) return Math.sign(dx);
  }
  if (shoulder && hip) {
    const dx = shoulder.x - hip.x;
    if (Math.abs(dx) > 1e-4) return Math.sign(dx);
  }
  return ctx.side === 'left' ? -1 : 1;
}

/** Torso length in plane units; the normalizer for translation-free ratios. */
export function torsoLength(ctx: MetricContext): number | null {
  const idx = sideLandmarks(ctx.side);
  const shoulder = p(ctx, idx.shoulder);
  const hip = p(ctx, idx.hip);
  if (!shoulder || !hip) return null;
  const len = distance(shoulder, hip);
  return len > 1e-6 ? len : null;
}

/**
 * Tragus estimate: the ear landmark nudged toward the outer eye corner
 * (HEAD_NECK_MODEL.tragusTowardEye). Falls back to the ear itself when the eye
 * corner is missing.
 */
export function estimateTragus(ctx: MetricContext): Point | null {
  const idx = sideLandmarks(ctx.side);
  const ear = p(ctx, idx.ear);
  if (!ear) return null;
  const eye = p(ctx, idx.eyeOuter);
  if (!eye) return ear;
  const k = HEAD_NECK_MODEL.tragusTowardEye;
  return { x: ear.x + (eye.x - ear.x) * k, y: ear.y + (eye.y - ear.y) * k };
}

/**
 * C7 estimate: above the shoulder along the trunk line and behind it, by the
 * anthropometric ratios in HEAD_NECK_MODEL. With the hip hidden, the trunk
 * line is taken as vertical and the shoulder–ear distance sets the scale.
 */
export function estimateC7(ctx: MetricContext): Point | null {
  const idx = sideLandmarks(ctx.side);
  const shoulder = p(ctx, idx.shoulder);
  if (!shoulder) return null;
  const hip = ctx.hipAvailable ? p(ctx, idx.hip) : null;
  const torso = hip ? distance(shoulder, hip) : 0;
  let up: Point;
  let above: number;
  if (hip && torso > 1e-6) {
    up = { x: (shoulder.x - hip.x) / torso, y: (shoulder.y - hip.y) / torso };
    above = HEAD_NECK_MODEL.c7AbovePerTorso * torso;
  } else {
    const ear = p(ctx, idx.ear);
    if (!ear) return null;
    up = { x: 0, y: -1 };
    above = HEAD_NECK_MODEL.c7AbovePerEarDistance * distance(shoulder, ear);
  }
  const behind = above / Math.tan(HEAD_NECK_MODEL.referenceSspDeg / RAD_TO_DEG);
  // Posterior unit vector: the trunk line turned away from the face.
  const a = anteriorSign(ctx);
  const posterior = { x: -a * -up.y, y: -a * up.x };
  return {
    x: shoulder.x + up.x * above + posterior.x * behind,
    y: shoulder.y + up.y * above + posterior.y * behind,
  };
}

/**
 * CVA — craniovertebral angle from the estimated C7 and tragus: the C7→tragus
 * line against the horizontal through C7. 90° when the tragus sits straight
 * above C7, smaller as the head travels forward. Thresholds: THRESHOLDS.cva.
 */
export function craniovertebralAngle(ctx: MetricContext): number | null {
  const tragus = estimateTragus(ctx);
  const c7 = estimateC7(ctx);
  if (!tragus || !c7) return null;
  return signedAngleFromHorizontal(sub(tragus, c7), anteriorSign(ctx));
}

/**
 * CHA — craniohorizontal angle: the tragus→outer-eye-corner line against the
 * horizontal, signed. Positive = eye above the tragus (chin up), negative =
 * looking down. Null when the eye corner is not clearly visible.
 */
export function craniohorizontalAngle(ctx: MetricContext): number | null {
  const idx = sideLandmarks(ctx.side);
  const eyeLm = ctx.landmarks[idx.eyeOuter];
  if (!eyeLm || eyeLm.visibility < THRESHOLDS.gate.earVisibility) return null;
  const tragus = estimateTragus(ctx);
  if (!tragus) return null;
  return signedAngleFromHorizontal(sub(toPlane(eyeLm, ctx.aspect), tragus), anteriorSign(ctx));
}

/**
 * Trunk inclination — hip→shoulder line against the vertical, signed.
 * Positive = leaning forward (anterior), negative = reclining.
 * Source: office ergonomics recommends a 90–110° trunk-thigh angle (OSHA eTool),
 * which maps to roughly −5°…+10° of lean.
 */
export function trunkInclination(ctx: MetricContext): number | null {
  const idx = sideLandmarks(ctx.side);
  const shoulder = p(ctx, idx.shoulder);
  const hip = p(ctx, idx.hip);
  if (!shoulder || !hip || !ctx.hipAvailable) return null;
  return signedAngleFromVertical(sub(shoulder, hip), anteriorSign(ctx));
}

/**
 * Shoulder protraction — anterior shoulder offset from the hip, divided by torso
 * length so it is scale- and distance-invariant. Positive = shoulder ahead of hip.
 */
export function shoulderProtraction(ctx: MetricContext): number | null {
  const idx = sideLandmarks(ctx.side);
  const shoulder = p(ctx, idx.shoulder);
  const hip = p(ctx, idx.hip);
  const torso = torsoLength(ctx);
  if (!shoulder || !hip || torso === null || !ctx.hipAvailable) return null;
  return (anteriorSign(ctx) * (shoulder.x - hip.x)) / torso;
}

/** Hip angle — shoulder–hip–knee. Chair ergonomics target 90–110° (OSHA eTool). */
export function hipAngle(ctx: MetricContext): number | null {
  const idx = sideLandmarks(ctx.side);
  const shoulder = p(ctx, idx.shoulder);
  const hip = p(ctx, idx.hip);
  const knee = p(ctx, idx.knee);
  if (!shoulder || !hip || !knee || !ctx.hipAvailable || !ctx.kneeAvailable) return null;
  return threePointAngle(shoulder, hip, knee);
}

/** Knee angle — hip–knee–ankle. Reference metric only. */
export function kneeAngle(ctx: MetricContext): number | null {
  const idx = sideLandmarks(ctx.side);
  const hip = p(ctx, idx.hip);
  const knee = p(ctx, idx.knee);
  const ankle = p(ctx, idx.ankle);
  if (!hip || !knee || !ankle || !ctx.kneeAvailable) return null;
  return threePointAngle(hip, knee, ankle);
}

/** Elbow angle — shoulder–elbow–wrist. Reference metric only (typing posture). */
export function elbowAngle(ctx: MetricContext): number | null {
  const idx = sideLandmarks(ctx.side);
  const shoulder = p(ctx, idx.shoulder);
  const elbow = p(ctx, idx.elbow);
  const wrist = p(ctx, idx.wrist);
  if (!shoulder || !elbow || !wrist) return null;
  return threePointAngle(shoulder, elbow, wrist);
}

/** Wrist deviation — how far the hand bends off the forearm line. Reference only. */
export function wristDeviation(ctx: MetricContext): number | null {
  const idx = sideLandmarks(ctx.side);
  const elbow = p(ctx, idx.elbow);
  const wrist = p(ctx, idx.wrist);
  const hand = p(ctx, idx.index);
  if (!elbow || !wrist || !hand) return null;
  return vectorAngle(sub(wrist, elbow), sub(hand, wrist));
}

export function computeFrameMetrics(ctx: MetricContext): FrameMetrics {
  return {
    cva: craniovertebralAngle(ctx),
    cha: craniohorizontalAngle(ctx),
    trunkInclination: trunkInclination(ctx),
    shoulderProtraction: shoulderProtraction(ctx),
    hipAngle: hipAngle(ctx),
    kneeAngle: kneeAngle(ctx),
    elbowAngle: elbowAngle(ctx),
    wristDeviation: wristDeviation(ctx),
  };
}

/** Convenience wrapper used by the debug page and tests. */
export function metricsFromLandmarks(
  landmarks: readonly Landmark[],
  side: Side,
  aspect: number,
): FrameMetrics {
  const idx = sideLandmarks(side);
  const hipVis = landmarks[idx.hip]?.visibility ?? 0;
  const kneeVis = landmarks[idx.knee]?.visibility ?? 0;
  return computeFrameMetrics({
    landmarks,
    side,
    aspect,
    hipAvailable: hipVis >= THRESHOLDS.gate.hipVisibility,
    kneeAvailable: kneeVis >= THRESHOLDS.gate.kneeVisibility,
  });
}
